import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const EMPTY = { shop_id: '', bond_date: '', amount: '', image_path: null };

function BondsModal({ initial, shops, onClose, onSave }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(initial?.image_path ? `https://meegoda.upload.lk/backend/uploads/${initial.image_path}` : null);
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    setForm(initial || EMPTY);
    setImageFile(null);
    setImagePreview(initial?.image_path ? `https://meegoda.upload.lk/backend/uploads/${initial.image_path}` : null);
  }, [initial]);

  const submit = async () => {
    if (!form.shop_id || !form.bond_date || !form.amount) {
      toast.error('Shop, date, and amount are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(form, imageFile);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleImageSelect = e => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>{initial?.id ? 'Edit Issue Bond' : 'Add Issue Bond'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Shop *</label>
            <select className="form-select" value={form.shop_id} onChange={set('shop_id')}>
              <option value="">— Select Shop —</option>
              {shops.map(s => (
                <option key={s.id} value={s.id}>{s.shop_code}</option>
              ))}
            </select>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input className="form-input" type="date" value={form.bond_date} onChange={set('bond_date')} />
            </div>
            <div className="form-group">
              <label className="form-label">Amount *</label>
              <input className="form-input" type="number" step="0.01" value={form.amount} onChange={set('amount')} placeholder="0.00" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Image</label>
            <input className="form-input" type="file" accept="image/*" onChange={handleImageSelect} />
            {imagePreview && (
              <div style={{ marginTop: 12 }}>
                <img src={imagePreview} alt="Bond proof" style={{ width: 120, borderRadius: 8, objectFit: 'cover' }} />
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function IssuesBonds({ member, shops = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoad] = useState(false);
  const [modal, setModal] = useState(null);
  const [deleting, setDel] = useState(null);

  useEffect(() => {
    if (!member?.id) {
      setRows([]);
      return;
    }
    setLoad(true);
    api.get(`/bonds/bonds/${member.id}`)
      .then(r => setRows(r.data || []))
      .catch(() => toast.error('Failed to load issue bonds'))
      .finally(() => setLoad(false));
  }, [member?.id]);

  const uploadBondImage = async (id, imageFile) => {
    const fd = new FormData();
    fd.append('image', imageFile);
    const { data } = await api.post(`/bonds/bonds/${id}/upload`, fd);
    return data;
  };

  const handleSave = async (form, imageFile) => {
    try {
      if (modal.row?.id) {
        const { data } = await api.put(`/bonds/bonds/${modal.row.id}`, form);
        const saved = imageFile ? await uploadBondImage(data.id, imageFile) : data;
        setRows(p => p.map(r => r.id === saved.id ? saved : r));
        toast.success('Updated');
      } else {
        const { data } = await api.post('/bonds/bonds', { member_id: member.id, ...form });
        const saved = imageFile ? await uploadBondImage(data.id, imageFile) : data;
        setRows(p => [...p, saved]);
        toast.success('Added');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this issue bond?')) return;
    setDel(id);
    try {
      await api.delete(`/bonds/bonds/${id}`);
      setRows(p => p.filter(r => r.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    } finally {
      setDel(null);
    }
  };

  if (!member?.id) return (
    <div className="empty-state">
      <p>Save Personal info first to manage issue bonds.</p>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => setModal({ row: null })}>
          + Add Issue Bond
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 32 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty-state"><p>No issue bonds added yet.</p></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table>
            <thead>
              <tr>
                <th>Shop</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Image</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td>{shops.find(s => s.id === row.shop_id)?.shop_code || `#${row.shop_id}`}</td>
                  <td>{row.bond_date}</td>
                  <td>Rs. {Number(row.amount).toFixed(2)}</td>
                  <td>
                    {row.image_path ? (
                      <a href={`https://meegoda.upload.lk/backend/uploads/${row.image_path}`} target="_blank" rel="noreferrer" className="badge badge-blue">
                        View
                      </a>
                    ) : (
                      <span style={{ color: 'var(--text3)' }}>—</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setModal({ row })}>Edit</button>
                      <button className="btn btn-danger btn-sm" disabled={deleting === row.id}
                        onClick={() => handleDelete(row.id)}>
                        {deleting === row.id ? '…' : 'Del'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal !== null && (
        <BondsModal
          initial={modal.row}
          shops={shops}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}